/**
 * ToolUnavailableBanner — rendered when a tool's PostHog kill-switch
 * (`tool_killswitch` flag) is flipped to that tool's id.
 *
 * Purpose: degrade gracefully when we need to disable a tool org-wide
 * (Gemini cost spike, regression mid-shift, scheduled maintenance)
 * without shipping a new build. Teachers see a friendly explanation
 * instead of a broken UI; the rest of the dashboard keeps working.
 *
 * Visual is intentionally calm — amber-toned advisory card, not a red
 * error. We're saying "this specific tool is paused," not "everything
 * is broken."
 */
import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import DashboardShell from "../layout/DashboardShell";

interface ToolUnavailableBannerProps {
  toolName: string;
}

export default function ToolUnavailableBanner({
  toolName,
}: ToolUnavailableBannerProps) {
  return (
    <DashboardShell>
      <div className="max-w-2xl mx-auto py-14 px-6">
        <div
          role="status"
          aria-live="polite"
          className="bg-amber-50 border border-amber-200 rounded-2xl shadow-sm p-6"
        >
          <div className="flex items-start gap-3">
            <AlertCircle
              className="w-5 h-5 text-amber-600 mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <div className="flex-1">
              <h1 className="text-lg font-bold text-stone-900">
                {toolName} is temporarily unavailable
              </h1>
              <p className="text-sm text-stone-700 mt-1">
                We've paused this tool for a quick fix. Everything else on
                TeacherDashboard still works. Your account, history, and
                other comment tools are unaffected.
              </p>
              <p className="text-sm text-stone-600 mt-2">
                We'll have it back up shortly. If you need help in the
                meantime, email{" "}
                <a
                  href="mailto:hello@teacherdashboard.ai"
                  className="text-brand-700 hover:underline"
                >
                  hello@teacherdashboard.ai
                </a>
                .
              </p>
              <div className="mt-5">
                <Link
                  to="/dashboard"
                  className="inline-flex items-center px-5 py-2.5 rounded-xl bg-stone-900 text-white text-sm font-semibold hover:bg-stone-800 transition-colors"
                >
                  Back to dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}
